import { useEffect, useState, type ReactNode } from 'react';

/**
 * Two panes with a fixed divider between them.
 *
 * The divider is a visual boundary only: it cannot be dragged, and the first
 * pane is always `initial` px, so every screen looks the same for everyone.
 * Below `collapseBelow` the whole thing becomes one column — a 1100px laptop
 * cannot spare 260px of navigator.
 */
export function SplitView({
  direction = 'row',
  initial,
  collapseBelow = 1100,
  first,
  second,
}: {
  /** Kept so call sites stay stable; the layout no longer stores anything per id. */
  id: string;
  direction?: 'row' | 'column';
  initial: number;
  collapseBelow?: number;
  first: ReactNode;
  second: ReactNode;
  firstLabel?: string;
}) {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < collapseBelow,
  );

  useEffect(() => {
    function onResize() {
      setNarrow(window.innerWidth < collapseBelow);
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [collapseBelow]);

  // One element tree for both layouts. Returning a different tree when the
  // width crosses `collapseBelow` made React throw away both panes and build
  // them again, so browser zoom (which changes the width) wiped half-typed
  // forms. Only classes and the divider's `hidden` change here.
  return (
    <div
      className={narrow ? 'splitview splitview--stacked' : `splitview splitview--${direction}`}
      style={
        narrow
          ? undefined
          : {
              gridTemplateColumns: direction === 'row' ? `${initial}px auto 1fr` : undefined,
              gridTemplateRows: direction === 'column' ? `${initial}px auto 1fr` : undefined,
            }
      }
    >
      <div className={narrow ? undefined : 'splitview__pane'}>{first}</div>

      <div
        className="splitview__splitter"
        hidden={narrow}
        role="separator"
        aria-orientation={direction === 'row' ? 'vertical' : 'horizontal'}
      />

      <div className={narrow ? undefined : 'splitview__pane splitview__pane--main'}>{second}</div>
    </div>
  );
}
